import { FunctionComponent } from 'react'

import translate, { Translations } from '@i18n'
import { formatDate } from '@utils/date'

import { CommentAuthorType, CommentingItemProps } from './types'
import styled, { useTheme } from 'styled-components'
import ItemContent from './ItemContent'
import SvgIcon from '@components/SvgIcon'
import ActionReact from './ActionReact'
import AnswerIndicator from './AnswerIndicator'
import ItemSuperUserIndicator from './ItemSuperUserIndicator'
import ItemHeader from './ItemHeader'
import DatePublished from './DatePublished'
import TextContent from './TextContent'
import ActionsResponding from './ActionsResponding'
import useReactions from './useReactions'
import PrimaryButton from '@components/Buttons/Primary'
import SecondaryButton from '@components/Buttons/Secondary'

const StyledSvgIcon = styled(SvgIcon)`
  margin-right: 6px;
`

const AuthorName = styled.div`
  flex: 1;
`

const ItemActions = styled.div`
  margin-top: 25px;
  display: flex;
  flex-wrap: wrap;
`

const ActionsReacting = styled.div`
  display: flex;
  align-items: center;
  line-height: 16px;
  margin-bottom: 15px;
`

const StyledSecondaryButton = styled(SecondaryButton)`
  margin-right: 10px;
`

const CommentingItem: FunctionComponent<CommentingItemProps> = ({
  type,
  authorType,
  gender,
  deleting,
  allowReplies,
  allowReports,
  isOwnItem,
  isTopRated,
  authorName,
  datePublished,
  textContent,
  onDelete,
  onReport,
  onReply,
  commentOrAnswer,
}) => {
  const [reactionProps] = useReactions(commentOrAnswer)
  const theme = useTheme()

  const {
    heartCount,
    boltCount,
    happyCount,
    sadCount,
    criticalCount,
    ownHeart,
    ownBolt,
    ownHappy,
    ownSad,
    ownCritical,
    onReactionHeart,
    onReactionBolt,
    onReactionHappy,
    onReactionSad,
    onReactionCritical,
  } = reactionProps

  const isSuperUser = [
    CommentAuthorType.ADMIN,
    CommentAuthorType.JOURNALIST,
    CommentAuthorType.MODERATOR,
  ].includes(authorType)

  type GetAuthorTranslationKeyType = () => keyof Translations
  const getAuthorTranslationKey: GetAuthorTranslationKeyType = () => {
    switch (authorType) {
      case CommentAuthorType.ADMIN:
        return gender === 'female'
          ? 'commenting.authorType.admin.female'
          : 'commenting.authorType.admin'
      case CommentAuthorType.JOURNALIST:
        return gender === 'female'
          ? 'commenting.authorType.journalist.female'
          : 'commenting.authorType.journalist'
      case CommentAuthorType.MODERATOR:
        return gender === 'female'
          ? 'commenting.authorType.moderator.female'
          : 'commenting.authorType.moderator'
      default:
        return 'commenting.authorType.user'
    }
  }

  return (
    <>
      {type === 'answer' && <AnswerIndicator />}
      <ItemContent
        isOwnItem={isOwnItem}
        deletingItem={deleting}
        isSuperUser={isSuperUser}>
        {isSuperUser ? (
          <ItemSuperUserIndicator
            authorType={
              translate(getAuthorTranslationKey()) as CommentAuthorType
            }
          />
        ) : null}
        <ItemHeader>
          {isTopRated && (
            <StyledSvgIcon
              iconName="newsticker-star-big"
              size={19}
              color={theme.colors.secondary.star_yellow}
            />
          )}
          <AuthorName>{authorName}</AuthorName>
          <DatePublished>
            {formatDate(datePublished)
              ? (formatDate(datePublished) as any)?.dateOrTime
              : ''}
          </DatePublished>
        </ItemHeader>
        <TextContent>{textContent}</TextContent>
        <ItemActions>
          <ActionsReacting>
            <ActionReact
              name="heart"
              onAction={onReactionHeart}
              hasOwnReaction={ownHeart}>
              {heartCount}
            </ActionReact>
            <ActionReact
              name="bolt"
              onAction={onReactionBolt}
              hasOwnReaction={ownBolt}>
              {boltCount}
            </ActionReact>
            <ActionReact
              name="happy"
              onAction={onReactionHappy}
              hasOwnReaction={ownHappy}>
              {happyCount}
            </ActionReact>
            <ActionReact
              name="sad"
              onAction={onReactionSad}
              hasOwnReaction={ownSad}>
              {sadCount}
            </ActionReact>
            <ActionReact
              name="critical"
              onAction={onReactionCritical}
              hasOwnReaction={ownCritical}>
              {criticalCount}
            </ActionReact>
          </ActionsReacting>
          <ActionsResponding>
            {isOwnItem ? (
              <StyledSecondaryButton onClick={onDelete} size="large">
                {translate('commenting.delete')}
              </StyledSecondaryButton>
            ) : (
              !isSuperUser &&
              allowReports && (
                <StyledSecondaryButton onClick={onReport} size="large">
                  {translate('commenting.report')}
                </StyledSecondaryButton>
              )
            )}
            {allowReplies && (
              <PrimaryButton onClick={onReply} size="large">
                {translate('commenting.answer')}
              </PrimaryButton>
            )}
          </ActionsResponding>
        </ItemActions>
      </ItemContent>
    </>
  )
}

export default CommentingItem
